import { ajaxCall } from "@/shared/connection/ajax-call";
import { makeLogger } from "@/shared/consola";
import { handleErrorByMessage } from "@/shared/connection/with-store-error-handlers";
import deepmerge from "deepmerge";
import { cloneDeep } from "@/shared/utils/helpers";
import store from "@/store";
import { ServerResponseError } from "@/shared/connection/ServerResponseError";

const makeSnapShotEndpoint = (tool, projectUuid) => `/project-snapshots/${tool}/${projectUuid}`;

const makeSnapshotKey = (stateKey) => `${stateKey}__snapshot`;

export function createSnapshotService(tool, modulePath = "") {
    const watchers = {};
    const stateKeys = new Set();
    const logger = makeLogger(`snapshot.service[for/${tool}]`);
    return {
        // makeSnapshotKey: (stateKey) => `${stateKey}__snapshot`,
        updateSnapshotAfterSave(uuid, { stateSlice, actionToTakeIfNotRequired = async () => {} }) {
            logger.debug("Update snapshot", stateKeys);
            const snapshotToUpdate = {};
            for (const key of stateKeys) {
                snapshotToUpdate[key] = stateSlice[makeSnapshotKey(key)];
            }
            return ajaxCall
                .put(makeSnapShotEndpoint(tool, uuid), snapshotToUpdate)
                .then(() => {
                    stateSlice.hasChanges = false;
                    stateSlice.hasSnapshot = true;
                    logger.debug(
                        "updateSnapshotAfterSave | hasSnapshot set to TRUE | Project HAS SNAPSHOT - it is not a draft!"
                    );
                })
                .catch(async (err) => {
                    // Checking if SNAPSHOT is NOT_REQUIRED
                    if (err instanceof ServerResponseError) {
                        if (
                            err.errorToken ===
                            ServerResponseError.SPECIAL_ERROR_TOKEN.SNAPSHOT_NOT_REQUIRED
                        ) {
                            logger.debug(
                                "SNAPSHOT_NOT_REQUIRED | project is DRAFT and logic for actionToTakeIfNotRequired will FIRE!"
                            );
                            await actionToTakeIfNotRequired();
                        } else {
                            // rethrow
                            throw err;
                        }
                    } else {
                        // rethrow
                        throw err;
                    }
                })
                .catch(handleErrorByMessage());
        },
        clearSnapshot(state, stateKey, value) {
            const snapshotKey = makeSnapshotKey(stateKey);
            state[snapshotKey] = value;
            state.hasChanges = false;
            state.hasSnapshot = false;
            logger.trace("clearSnapshot | hasSnapshot: FALSE");
        },
        makeSnapshotState(state, stateKey, projectData, snapshotData) {
            if (!state[stateKey]) {
                throw new Error(`Cannot snapshot state that does not exist (key: "${stateKey}")`);
            }
            const isArray = Array.isArray(state[stateKey]);
            const snapshotKey = makeSnapshotKey(stateKey);
            const snapshotValue = cloneDeep(
                snapshotData[stateKey] || (isArray ? projectData || [] : {})
            );
            state[snapshotKey] = snapshotValue;
            stateKeys.add(stateKey);
            const computedState = deepmerge(cloneDeep(projectData), snapshotValue, {
                // If merged element is an array -> choose snapshot value to overwrite it!
                arrayMerge: (d, snapshot) => snapshot,
            });
            state.hasChanges = false;
            // This condition is important, because some other snapshotData inside can already set this to true!
            if (!state.hasSnapshot) {
                state.hasSnapshot = Boolean(Object.keys(snapshotData || {}).length);
                logger.trace(
                    "makeSnapshotState | hasSnapshot calculated to be:",
                    state.hasSnapshot
                );
            }
            if (watchers[stateKey]) {
                // unwatch previous if exist
                logger.debug("Unwatch previous", stateKey);
                watchers[stateKey]();
                watchers[stateKey] = undefined;
            }
            setTimeout(() => {
                // This timeout is CRUCIAL to skip first change of properties (initialization) being treated as hasChanges!
                watchers[stateKey] = store.watch(
                    (state) => state[modulePath][stateKey],
                    () => {
                        state.hasChanges = true;
                        logger.debug(
                            "hasChanges changed to true, due to changes of --",
                            stateKey,
                            "--"
                        );
                    },
                    { deep: true, immediate: false }
                );
            });
            return new Proxy(computedState, {
                set: (target, key, value) => {
                    target[key] = value;
                    state[snapshotKey][key] = value;
                    // This is not enough for deeply nested objects! (we still need a watchers!)
                    state.hasChanges = true;
                    return true;
                },
            });
        },
    };
}
